<template>
  <div v-if="isAdmin || isManager || isTalent" class="font-inter min-h-screen bg-[#FFFFFF] rounded-t-[20px]">
    <div v-if="activityLimit && (activityLimit.is_limit_reached || activityLimit.display_warning_banner)" class="mx-8 mt-4">
      <div v-if="activityLimit.is_limit_reached" class="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-2">
        You have reached the maximum number of activities that can be recorded.
      </div>
      <div v-else-if="activityLimit.display_warning_banner" class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-2 rounded mb-2">
        Warning: Only {{ activityLimit.max_count - activityLimit.current_count }} activities left to be recorded.
      </div>
    </div>
    <!-- <Navbar /> -->
    <div class="flex mx-8 space-x-6 py-1 text-[14px] rounded-[8px] items-center">
      <div class="flex gap-4 justify-between items-center text-grayDark">
        <div class="flex gap-4">
          <p class="flex items-center gap-2 rounded-[8px]">
            <img src="@/assets/projects/filter.svg" alt="Icon" class="">
            Filters
          </p>
          <div class="flex items-center gap-4 ">
            <!-- Talent Filter (Modal-based) -->
            <div class=" " ref="dropdown" @click="stopEventPropagation">
              <div class="flex items-center gap-2 px-2 py-1 border border-grayLight rounded-[8px] cursor-pointer"
                @click="openTalentModal">
                {{ globalSelectedTalent || "Talent" }}
                <img src="@/assets/projects/chevron-down.svg" alt="Dropdown Icon" />
              </div>
            </div>


            <!-- Date filter button -->
            <button @click="showDateFilter = true" class="px-3 py-1 border rounded-[8px] flex items-center"
              >
              <span v-if="!hasDateFilter">Date</span>
              <span v-else class="text-bodyText">Date range
                <span class="text-grayDark">
                  {{ formatDateForDisplay(startDate) }} {{ endDate ? '- ' + formatDateForDisplay(endDate) : '' }}

                </span> 
              </span>
              <!-- <span v-if="hasDateFilter"
                class="ml-2 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {{ startDate && endDate ? '2' : '1' }}
              </span> -->
            </button>

            <!-- Separate display of applied filters with clear buttons -->
            <!-- <div v-if="hasDateFilter" class="flex items-center gap-2">
              <button @click="clearAllDateFilters" class="ml-1 text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100">×</button>
            </div> -->

            <!-- Clear all filters button -->
            <!-- <button v-if="hasAnyFilter" @click="clearAllFilters" class="px-3 py-1 text-red-600 hover:bg-red-50 rounded">
              Clear filters
            </button> -->

            <!-- Date filter modal -->
            <ActivityDateFilter :isOpen="showDateFilter" :initialStartDate="startDate" :initialEndDate="endDate"
              @close="showDateFilter = false" @apply="applyDateFilter" />

          </div>
          <!-- <div class="relative" ref="timeDropdown">
            <div class="flex items-center gap-2 px-2 py-1 border border-grayLight rounded cursor-pointer"
              @click="toggleTimeDropdown">
              {{ selectedTimeFilter.label }}
              <img src="@/assets/projects/chevron-down.svg" alt="Dropdown Icon" />
            </div>
            <div v-if="showTimeDropdown" class="absolute bg-white border rounded shadow mt-2">
              <ul>
                <li v-for="option in timeFilterOptions" :key="option.value" @click="setTimeFilter(option)"
                  class="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                  {{ option.label }}
                </li>
              </ul>
            </div>
          </div> -->
        </div>
        <div v-if="isAdmin || isManager || isTalent" class="flex gap-4 items-center">
          <p>Total time spent {{ formattedTotalTime }}</p>
        </div>
      </div>
    </div>
    <hr class="w-full border-t-[1px] border-grayLight rounded-full">

    <!-- Loading indicator -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"></div>
    </div>

    <div v-else class="mx-8 mt-6 mb-16">
      <!-- No data message when filters are applied but no results -->
      <div v-if="!isLoading && activities.length === 0" class="text-center text-bodyText">
        <p class="mx-auto bg-primaryLight px-2 py-24 border border-grayLight rounded-[16px]">
          {{ hasAnyFilter ? "No activity data found with the selected filters" : "No activity data available for this project" }}
        </p>
      </div>
      <div v-if="activities.length > 0" class="py-3 px-6 items-center">
        <div v-for="(activity) in activities" :key="activity.activity_id"
          class="bg-[#FAFBFC] p-4 space-y-4 mb-4 rounded-lg text-grayDark text-[14px]">
          <div class="flex justify-between items-center">
            <div class="flex gap-3 items-center">
              <img src="@/assets/projects/clock.svg" alt="Icon" class="h-4 w-4" />
              <p>
                <!-- {{ activity.start_time }} - {{ activity.end_time }} -->
                    {{ formatActivityDateTime(activity.start_time) }} - {{ formatActivityDateTime(activity.end_time) }}

              </p>
            </div>
            <div class="flex items-center">
              <div class="relative w-[268px] h-[9px] bg-[#F3F4F6] rounded-full overflow-hidden">
                <div class="absolute top-0 left-0 h-full bg-primaryDarkBg rounded-full"
                  :style="{ width: `${activity.percent}%` }"></div>
              </div>
              <span class="ml-2 text-[12px] font-medium">{{ activity.percent }}%</span>
            </div>
          </div>

          <div class="flex gap-2 items-center">
            <img src="@/assets/projects/task_notes.svg" alt="Icon" class="h-4 w-4" />
            <p v-if="activity.note" class="">{{ activity.note }}</p>
            <p v-else>No activity notes</p>
          </div>
          <div class="flex gap-2 items-center">
            <img src="@/assets/projects/task_notes.svg" alt="Icon" class="h-4 w-4" />
            <p v-if="activity.talent_first_name" class="">{{ activity.talent_first_name + ' ' +
              activity.talent_last_name }} - {{ activity.talent_email }}</p>
            <p v-else>No user data</p>
          </div>
          <div class="flex gap-2 items-center">
            <img src="@/assets/projects/gallery.svg" alt="Icon" class="h-4 w-4" />
            <p class="">
              Attachments ({{ activity.thumbnails ? activity.thumbnails.length : 0 }})
            </p>
          </div>
          <hr class="w-full border-t-[1px] border-grayLight rounded-full">
          <div v-if="activity.thumbnails && activity.thumbnails.length" class="flex gap-4 overflow-x-auto">
            <img v-for="(thumbnail, index) in activity.thumbnails" :key="index" :src="thumbnail" loading="lazy"
              alt="Activity Thumbnail" class="w-[100px] h-[100px] object-cover rounded-lg shadow-sm cursor-pointer"
              @click="openFullImage(activity.images ? activity.images[index] : thumbnail)" />
          </div>
          <div v-else class="text-[14px]">
            <p class=" mx-auto bg-primaryLight px-2 py-24 border border-grayLight rounded-[16px]">
              No images available for this activity.
            </p>
          </div>
        </div>
      </div>
      <PaginationComponent v-if="activities.length > 0" :currentPage="currentPage" :totalPages="totalPages" @changePage="handlePageChange" />
    </div>
    <div v-if="showImageModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      @click="showImageModal = false">
      <div class="relative p-4 bg-white rounded-lg max-w-[90%] max-h-[90%]">
        <img :src="selectedImage" alt="Full Image" class="max-w-full max-h-[80vh] rounded-lg" />
      </div>
    </div>

    <!-- Talent Selection Modal -->
    <Teleport to="body">
      <div
        v-if="isTalentModalOpen"
        class="fixed inset-0 flex items-center justify-center z-50"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="closeTalentModal"
        ></div>

        <!-- Modal -->
        <div
          class="bg-white max-w-lg w-full max-h-[90vh] p-6 rounded-lg shadow-lg relative z-50 flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex justify-between items-center border-b pb-3 sticky top-0 bg-white z-10"
          >
            <h3 class="text-lg font-medium">Search Talent Name</h3>
            <button
              @click="closeTalentModal"
              class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-100"
            >
              <img
                src="@/assets/tasks/iconoir_cancel.svg"
                alt="Close"
                class="h-5 w-5"
              />
            </button>
          </div>

          <!-- Search Input -->
          <div class="relative mt-4 sticky top-[50px] bg-white z-10">
            <input
              v-model="talentSearchQuery"
              type="text"
              class="w-full h-11 px-3 pl-10 rounded-md shadow-sm bg-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter a keyword"
              required
            />
            <button
              class="absolute top-1/2 left-3 transform -translate-y-1/2 cursor-pointer"
            >
              <img
                src="@/assets/projects/search-normal.svg"
                alt="Search"
                class="h-5 w-5"
              />
            </button>
          </div>

          <!-- Scrollable Content -->
          <div class="py-4 overflow-y-auto flex-1">
            <!-- Clear Filter Option -->
            <div class="mb-4">
              <div
                class="flex gap-3 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-100 p-2 rounded border-b border-gray-200"
                @click="clearTalentFilter"
              >
                <div
                  class="w-[42px] h-[42px] text-[14px] flex items-center justify-center rounded-full text-gray-600 font-semibold bg-gray-200"
                >
                  All
                </div>

                <div class="text-[13px]">
                  <p class="font-medium text-gray-900">All Talents</p>
                  <p class="text-grayDark">Show activities from all team members</p>
                </div>
              </div>
            </div>

            <div v-if="filteredMembers.length > 0" class="space-y-3">
              <div
                v-for="member in filteredMembers"
                :key="member.project_member_id"
                class="relative"
              >
                <div
                  class="flex gap-3 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-100 p-2 rounded"
                  @click="selectTalentFromModal(member)"
                >
                  <div
                    class="w-[42px] h-[42px] text-[14px] flex items-center justify-center rounded-full text-white font-semibold bg-[#A3DB7D]"
                  >
                    {{
                      getInitials(
                        member.talent_first_name,
                        member.talent_last_name
                      )
                    }}
                  </div>

                  <div class="text-[13px]">
                    <p class="font-medium text-gray-900">
                      {{ capitalize(member.talent_first_name) }}
                      {{ capitalize(member.talent_last_name) }}
                    </p>
                    <p class="text-grayDark">{{ member.talent_email }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500 py-6">
              No talent found in project
            </div>
          </div>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
import apiClient from '@/axios';
// import Navbar from '../NavbarComponent.vue';
import PaginationComponent from '../PaginationComponent.vue';
import ActivityDateFilter from '../ActivityDateFilter.vue';

export default {
  name: 'ActivityData',
  components: {
    // Navbar,
    PaginationComponent,
    ActivityDateFilter,
  },
  props: {
    projectId: {
      type: String,
      required: true,
    },
    globalSelectedTalent: {
      type: String,
      default: null,
    },
    globalSelectedTalentId: {
      type: String,
      default: null,
    },
    globalStartDate: {
      type: String,
      default: "",
    },
    globalEndDate: {
      type: String,
      default: "",
    },
  },
  emits: ['update-global-filters'],
  data() {
    return {
      showDateFilter: false,
      startDate: '',
      endDate: '',
      
      // UI state
      isLoading: false,

      selectedDate: null, // Store the selected date

      showTimeDropdown: false,
      selectedTimeFilter: { label: "All Time", value: "all" },
      timeFilterOptions: [
        { label: "All Time", value: "all" },
        { label: "Today", value: "today" },
        { label: "This Week", value: "week" },
        { label: "This Month", value: "month" },
      ],
      filters: {
        talent: null,
      },

      showImageModal: false,
      selectedImage: null,
      activities: [],
      projectName: '',
      projectDescription: '',
      totalTimeSpent: '',
      tasks: [],
      talent: {
        talentEmail: '',
        talentName: '',
      },
      talents: [],
      members: [],
      timeSpent: null,
      selectedTalent: null, 
      showTalentDropdown: false,
      isTalentModalOpen: false,
      talentSearchQuery: "",
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      loggedInTalentId: null,
      activityLimit: null, // Store activity creation limit
    };
  },
  watch: {
    // Watch for changes in global props and update local state
    globalSelectedTalent: {
      handler(newVal) {
        if (newVal) {
          // Find the talent in members and set it
          const talent = this.members.find(m => m.talent_id === this.globalSelectedTalentId);
          this.selectedTalent = talent;
          this.filters.talent = this.globalSelectedTalentId;
        } else {
          this.selectedTalent = null;
          this.filters.talent = null;
        }
        
        // Fetch activities with the new talent filter
        this.currentPage = 1; // Reset to first page
        this.fetchActivities();
      },
      immediate: true,
    },
    globalStartDate: {
      handler(newVal) {
        this.startDate = newVal;
      },
      immediate: true,
    },
    globalEndDate: {
      handler(newVal) {
        this.endDate = newVal;
      },
      immediate: true,
    },
  },
  computed: {
    hasDateFilter() {
      return this.startDate || this.endDate;
    },
    hasAnyFilter() {
      return this.filters.talent || this.hasDateFilter || this.selectedTimeFilter.value !== 'all';
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },

    totalTimeInSeconds() {
      if (!this.totalTimeSpent || isNaN(this.totalTimeSpent)) {
        return 0;
      }
      return Number(this.totalTimeSpent);
    },

    totalTimeSpentInSeconds() {
      if (!this.timeSpent || isNaN(this.timeSpent)) {
        return 0;
      }
      return Number(this.timeSpent);
    },

    formattedTotalTimeSpent() {
      if (this.totalTimeSpentInSeconds <= 0) {
        return "0h 0m 0s";
      }
      const hours = Math.floor(this.totalTimeSpentInSeconds / 3600);
      const minutes = Math.floor((this.totalTimeSpentInSeconds % 3600) / 60);
      const seconds = this.totalTimeSpentInSeconds % 60;
      return `${hours}h ${minutes}m ${seconds}s`;
    },

    formattedTotalTime() {
      if (this.totalTimeInSeconds <= 0) {
        return "0h 0m 0s";
      }
      const hours = Math.floor(this.totalTimeInSeconds / 3600);
      const minutes = Math.floor((this.totalTimeInSeconds % 3600) / 60);
      const seconds = this.totalTimeInSeconds % 60;
      return `${hours}h ${minutes}m ${seconds}s`;
    },
    filteredMembers() {
      if (!this.talentSearchQuery) return this.members;

      const query = this.talentSearchQuery.toLowerCase();
      return this.members.filter((member) => {
        const fullName =
          `${member.talent_first_name} ${member.talent_last_name}`.toLowerCase();
        const email = member.talent_email.toLowerCase();
        return fullName.includes(query) || email.includes(query);
      });
    },
  },
  methods: {
    clearAllDateFilters() {
  this.startDate = '';
  this.endDate = '';
  this.currentPage = 1;
  this.saveFiltersToStorage();
  this.fetchActivities();
},
    applyDateFilter(data) {
      this.startDate = data.startDate;
      this.endDate = data.endDate;
      this.currentPage = 1; // Reset to first page when applying a filter
      
      // Emit to parent component
      this.$emit('update-global-filters', {
        globalStartDate: this.startDate,
        globalEndDate: this.endDate,
      });
      
      this.saveFiltersToStorage();
      this.fetchActivities();
    },

    clearStartDate() {
      this.startDate = '';
      this.currentPage = 1;
      this.saveFiltersToStorage();
      this.fetchActivities();
    },

    clearEndDate() {
      this.endDate = '';
      this.currentPage = 1;
      this.saveFiltersToStorage();
      this.fetchActivities();
    },

    clearAllFilters() {
      this.selectedTalent = null;
      this.filters.talent = null;
      this.startDate = '';
      this.endDate = '';
      this.selectedTimeFilter = this.timeFilterOptions[0]; // Reset to "All Time"
      this.currentPage = 1;
      this.saveFiltersToStorage();
      this.fetchActivities();
    },

    setTalentFilter(talent) {
      this.filters = {
        ...this.filters,
        talent: talent ? talent.talent_id : null,
      };
      this.selectedTalent = talent;
      this.showTalentDropdown = false;
      this.saveFiltersToStorage();
      this.fetchActivities(1); // Reset to page 1 and fetch with new filter
    },

    setTimeFilter(option) {
      this.selectedTimeFilter = option;
      this.showTimeDropdown = false;
      this.saveFiltersToStorage();
      this.fetchActivities(1); // Reset to page 1 and fetch with new time filter
    },
    
formatDateForDisplay(dateString) {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
},

formatActivityDateTime(dateTimeString) {
  const date = new Date(dateTimeString);
  const options = { 
    weekday: 'short', 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  };
  const dateStr = date.toLocaleDateString('en-US', options);
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour12: false, 
    hour: '2-digit', 
    minute: '2-digit' 
  });
  return `${dateStr}, ${timeStr}`;
},
    
    filterByDate() {
      this.$forceUpdate(); // Refresh the computed property
    },

    toggleTimeDropdown() {
      this.showTimeDropdown = !this.showTimeDropdown;
    },

    isWithinTimeRange(activity) {
      const now = new Date();
      const startTime = new Date(activity.start_time_unix * 1000); // Convert UNIX to JS Date

      switch (this.selectedTimeFilter.value) {
        case "today":
          return startTime.toDateString() === now.toDateString();

        case "week": {
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay()); // Start of the week (Sunday)
          return startTime >= startOfWeek;
        }

        case "month":
          return (
            startTime.getFullYear() === now.getFullYear() &&
            startTime.getMonth() === now.getMonth()
          );

        default:
          return true; // "All Time"
      }
    },

    openFullImage(image) {
      this.selectedImage = image;
      this.showImageModal = true;
    },
    
    closeDropdown() {
      this.showTalentDropdown = false;
    },
    
    stopEventPropagation(event) {
      event.stopPropagation();
    },
    
    // Modal methods for talent selection
    openTalentModal() {
      this.isTalentModalOpen = true;
    },

    closeTalentModal() {
      this.isTalentModalOpen = false;
      this.talentSearchQuery = "";
    },

    selectTalentFromModal(talent) {
      const talentName = `${this.capitalize(
        talent.talent_first_name
      )} ${this.capitalize(talent.talent_last_name)}`;
      
      // Emit to parent component
      this.$emit('update-global-filters', {
        globalSelectedTalent: talentName,
        globalSelectedTalentId: talent.talent_id,
      });
      
      this.closeTalentModal();
    },

    clearTalentFilter() {
      // Emit to parent component to clear the global filter
      this.$emit('update-global-filters', {
        globalSelectedTalent: null,
        globalSelectedTalentId: null,
      });
      
      this.closeTalentModal();
    },

    getInitials(firstName, lastName) {
      const firstInitial = firstName
        ? firstName.trim().charAt(0).toUpperCase()
        : "";
      const lastInitial = lastName
        ? lastName.trim().charAt(0).toUpperCase()
        : "";
      return `${firstInitial}${lastInitial}`;
    },

    capitalize(text) {
      if (!text) return "";
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },

    applyFilters() {
      console.log("Applying Filters:", this.filters);
      this.fetchActivities(1);
    },

    async fetchTalentData(projectId) {
      if (this.isTalent) {
        try {
          // Fetch data for the logged-in talent
          const response = await apiClient.get('/web/talent/me');
          const talentData = response.data.data;

          // Store the logged-in talent ID
          this.loggedInTalentId = talentData.talent_id;
          console.log('loggedInTalentId:', this.loggedInTalentId);

          // Fetch project members to get the time spent for the logged-in talent
          await this.fetchProjectMembers(this.$route.params.projectId);

          // Find the logged-in talent in project members
          const loggedInMember = this.members.find(member => member.talent_id === this.loggedInTalentId);

          if (loggedInMember) {
            this.timeSpent = loggedInMember.time_spent; // Store the time spent of the logged-in talent
          } else {
            console.log('Logged-in talent not found in project members.');
          }
          console.log('loggedInMember:', loggedInMember);

          // Fetch projects related to the logged-in talent
          this.projects = talentData.projects || [];
          console.log('Projects retrieved:', this.projects);
        } catch (error) {
          console.error('Error fetching talent data:', error);
        }
      } else if (this.isAdmin || this.isManager) {
        try {
          const response = await apiClient.get(`/web/projects/${projectId}`);
          // this.totalTimeSpent = response.data.data.time_spent;
          console.log('project_data', response.data.data)
        } catch (error) {
          console.error("Error fetching project time spent:", error);
        }
      }
    },

    async fetchActivities(page = 1) {
      const projectId = this.$route.params.projectId;
      this.isLoading = true; // Show loading indicator
      
      try {
        const response = await apiClient.get(`/web/projects/${projectId}/activities`, {
          params: {
            page: page,
            page_size: this.pageSize,
            talent_id: this.filters.talent || null,
            start_date: this.startDate || null,
            end_date: this.endDate || null,
            time_filter: this.selectedTimeFilter.value !== 'all' ? this.selectedTimeFilter.value : null,
          }
        });

        const { data, pagination } = response.data;
        this.totalTimeSpent = response.data.total_time_spent_query || '0';

        this.activities = data.map((activity) => ({
          ...activity,
          start_time_unix: activity.start_time,
          end_time_unix: activity.end_time,
          start_time: this.convertUnixTime(activity.start_time),
          end_time: this.convertUnixTime(activity.end_time),
        }));
        this.totalTimeSpent = response.data.total_time_spent_query;
        this.currentPage = page;
        this.totalPages = pagination?.total_pages || 1;

      } catch (error) {
        console.error("Error fetching activity data:", error);
        this.activities = []; // Reset activities on error
        this.totalTimeSpent = '0'; // Reset time spent on error

      } finally {
        this.isLoading = false; // Hide loading indicator
      }
    },
    
// Save filters to localStorage with user ID
saveFiltersToStorage() {
  // Get the current user ID from your Vuex store
  const currentUser = this.$store.state.user;
  const userId = currentUser ? currentUser.user_id : 'guest';
  
  const filters = {
    talent: this.filters.talent,
    talentObj: this.selectedTalent ? JSON.stringify(this.selectedTalent) : null,
    startDate: this.startDate,
    endDate: this.endDate,
    timeFilter: this.selectedTimeFilter.value
  };
  
  // Include user ID in the localStorage key
  localStorage.setItem(`user_${userId}_project_${this.$route.params.projectId}_filters`, JSON.stringify(filters));
},

loadFiltersFromStorage() {
  const currentUser = this.$store.state.user;
  const userId = currentUser ? currentUser.user_id : 'guest';
  const savedFilters = localStorage.getItem(`user_${userId}_project_${this.$route.params.projectId}_filters`);
  
  if (savedFilters) {
    const filters = JSON.parse(savedFilters);
    this.filters.talent = filters.talent;
    this.startDate = filters.startDate;
    this.endDate = filters.endDate;
    
    if (filters.talentObj) {
      this.selectedTalent = JSON.parse(filters.talentObj);
    }
    
    if (filters.timeFilter) {
      this.selectedTimeFilter = this.timeFilterOptions.find(opt => opt.value === filters.timeFilter) || 
                              this.timeFilterOptions[0];
    }
  }
},
    handlePageChange(newPage) {
      if (newPage > 0 && newPage <= this.totalPages) {
        this.fetchActivities(newPage);
      }
    },

    async fetchProjectMembers(projectId) {
      try {
        const response = await apiClient.get(`/web/projects/${projectId}/project_members/`);
        this.members = response.data.data;
      } catch (error) {
        console.error('Error fetching members data:', error);
      }
    },
    
    convertUnixTime(unixTime) {
      const date = new Date(unixTime * 1000);

      // Format date as "YYYY-MM-DD HH:mm:ss"
      return date.toISOString().replace("T", " ").split(".")[0];
    },

    goToProject(activityId) {
      this.$router.push({ name: 'ActivityDetails', params: { activityId } });
    },

    async fetchActivityLimit() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await apiClient.get('/web/limit/', { params: { id: projectId, limit_type: 'project', name: 'activities' } });
        this.activityLimit = response.data.data;
      } catch (error) {
        console.error('Error fetching activity limit:', error);
      }
    },
  },

  created() {
    const projectId = this.$route.params.projectId;
    this.fetchProjectMembers(projectId).then(() => {
      // Load filters after members are fetched so we can restore the selectedTalent object
      this.loadFiltersFromStorage();
      this.fetchActivities(this.currentPage);
    });
    this.fetchTalentData(projectId);
    this.fetchActivityLimit();
  },
  
  mounted() {
    document.addEventListener("click", this.closeDropdown);
  },
  
  beforeUnmount() {
    // Clean up the event listener
    document.removeEventListener("click", this.closeDropdown);
  },
};
</script>

<style scoped>
/* Add any scoped styles here */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>